import { conditionClassModifier } from '@surfline/web-common';
import classnames from 'classnames/bind';
import { scaleLinear, ScaleTime } from 'd3-scale';
import { select, selectAll } from 'd3-selection';
import { max } from 'lodash';
import { MutableRefObject, useCallback } from 'react';
import { useUnmount } from 'react-use';
import { LiveWindPoint } from 'types/liveWind';
import { ConditionRating } from '../../types/conditionRating';
import styles from './LiveWindBarGraph.module.scss';

const GRAPH_PADDING = 30;
const BAR_WIDTH = 2;
const BAR_PADDING_FROM_CENTER = 2;

const cx = classnames.bind(styles);

const getConditionsClassName = (className: string, conditions?: string) =>
  cx({
    [className]: true,
    [`sl-conditions-color--${conditionClassModifier(conditions)}-fill`]: !!conditions,
    [`${className}--noRating`]: !conditions,
  });

interface LiveWindWithRatings extends LiveWindPoint {
  condition: ConditionRating;
}

const useSetupBarGraphCanvas = (
  liveWindWithRatings: Array<LiveWindWithRatings>,
  xScale: ScaleTime<number, number>,
  currentMarkerXPercentage: number,
  canvasRef: MutableRefObject<HTMLDivElement | null>,
) => {
  const setUpBarGraph = useCallback(() => {
    select('.barGraphSVG').remove();
    if (canvasRef?.current) {
      const GRAPH_WIDTH = canvasRef.current.offsetWidth;
      const GRAPH_HEIGHT = canvasRef.current.offsetHeight;

      // calculate bar spacing based on set 2px bar width
      const BAR_SPACING =
        (GRAPH_WIDTH - liveWindWithRatings.length * BAR_WIDTH) / (liveWindWithRatings.length - 1);
      const currentTimestamp = +new Date() / 1000;
      // find max wind speed magnitude
      const maxY = max(
        liveWindWithRatings.map((reading) => Math.abs(reading.wind.speed)),
      ) as number;

      // extract readings where sensorWind = false and reading.timestamp is in
      // the past and add original liveWind array index as `position` to calculate offset
      const noSensorDataReadings = liveWindWithRatings.map((reading, i) =>
        !reading.sensorWind && currentTimestamp > reading.timestamp
          ? { ...reading, position: i }
          : null,
      );

      const canvas = select(`#onshore-offshore-graph-canvas`)
        .append('svg')
        .attr('height', GRAPH_HEIGHT)
        .attr('width', GRAPH_WIDTH)
        .attr('class', 'barGraphSVG')
        .on('mouseenter', function mousemove() {
          select(this).selectChildren().style('opacity', 0.5);
        })
        .on('mousemove', (event) => {
          // reset opacities to general graph hover state
          selectAll('.barGraphConditionBar').style('opacity', 0.5);
          selectAll('.barGraphReadingOffline').style('opacity', 1);

          // offset current mouse position by -0.5 to account for 1px TimeLine width
          const highlightIndex = Math.floor(
            liveWindWithRatings.length * ((event.offsetX - 0.5) / GRAPH_WIDTH),
          );
          const bars = selectAll('.barGraphConditionBar').nodes()[highlightIndex];
          const offlineGroups = selectAll('.barGraphReadingOffline').nodes()[highlightIndex];
          select(bars).style('opacity', 1);
          select(offlineGroups).style('opacity', 0.5);
        })
        .on('mouseleave', (event) => {
          // todo: there's probably a better way to do this that notices the mouse leaving the svg 100% of the time
          // but this should be good enough for now
          if (
            event.offsetX < 0 ||
            event.offsetY < 0 ||
            event.offsetX >= GRAPH_WIDTH ||
            event.offsetY >= GRAPH_HEIGHT
          ) {
            selectAll('.barGraphConditionBar').style('opacity', 1);
          }
        });

      const data = liveWindWithRatings;

      const regularDomain = [maxY * -1, maxY];
      const minScaleDomain = [-10, 10];

      const yScale = scaleLinear()
        .domain(maxY > 10 ? regularDomain : minScaleDomain)
        .range([-GRAPH_HEIGHT / 2 + GRAPH_PADDING, GRAPH_HEIGHT / 2 - GRAPH_PADDING]);

      const barYPosition = (reading: LiveWindWithRatings) => {
        const center = GRAPH_HEIGHT / 2;
        const speed = reading.wind.offshoreSpeed;
        const barBottomOffset = 3;
        const barPositionOffset = speed >= 0 ? yScale(speed) + barBottomOffset : -barBottomOffset;
        if (speed === 0) {
          return center;
        }
        return center - barPositionOffset;
      };

      const barHeight = (reading: LiveWindWithRatings) => {
        // set height of bar to 0 for sensorWind = false for past readings

        if (!reading.sensorWind && reading.timestamp <= currentTimestamp) {
          return 0;
        }
        return Math.abs(yScale(reading.wind.offshoreSpeed)) + BAR_PADDING_FROM_CENTER;
      };

      const noDataGroups = canvas
        .selectAll('g')
        .data(noSensorDataReadings)
        .enter()
        .append('g')
        .attr('transform', (d) => d && `translate(${xScale(d.timestamp) + 0.5},${0})`)
        .style('pointer-events', 'bounding-box')
        .attr('display', (d) => !d && 'none')
        .attr('class', 'barGraphReadingOffline')
        .attr('data-testid', 'live-wind-offline');

      noDataGroups
        .selectAll('circle')
        // array with corresponding y positions of 3 offline dots
        .data([-3, 0, 3])
        .enter()
        .append('circle')
        .attr('r', 1)
        .attr('cx', BAR_SPACING)
        .attr('cy', (d) => GRAPH_HEIGHT / 2 + 1 + d)
        .attr('class', styles.stationOfflineDot);

      canvas
        .selectAll('rect')
        .data(data)
        .enter()
        .append('rect')
        .attr('x', (d) => xScale(d.timestamp))
        .attr('y', (d) => barYPosition(d))
        .attr('height', (d) => barHeight(d))
        .attr('width', BAR_WIDTH)
        .attr('rx', (d) => (d.wind.speed === 0 ? 1 : 0))
        .attr('ry', (d) => (d.wind.speed === 0 ? 1 : 0))
        .attr('class', 'barGraphConditionBar')
        .attr('class', (d) => getConditionsClassName('barGraphConditionBar', d?.condition?.rating))
        .attr('data-testid', 'live-wind-bar');
    }
  }, [canvasRef, liveWindWithRatings, xScale]);

  useUnmount(() => {
    select('.barGraphSVG').remove();
  });

  return { setUpBarGraph };
};

export default useSetupBarGraphCanvas;
